import React, { useCallback, useEffect, useState } from "react";
import PageTitle from "../../../components/ui/PageTitle";
import EmptyState from "../../../components/ui/EmptyState";
import Button from "../../../components/ui/Button";
import Modal from "../../../components/ui/Modal";
import Input from "../../../components/ui/Input";
import TextArea from "../../../components/ui/Textarea";
import MultiSelect from "../../../components/ui/MultiSelect";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import { getAdminChannels, getAdminCombos, createAdminCombo } from "../../../lib/apiClient";
import { Channel, Combo } from "../../../types";
import ComboListItem from "../../../components/admin/ComboListItem";

const AdminComboPlansPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [combos, setCombos] = useState<Combo[]>([]);
  const [combosLoading, setCombosLoading] = useState(false);
  const [combosError, setCombosError] = useState<string | null>(null);

  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelError, setChannelError] = useState<string | null>(null);
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);

  const [name, setName] = useState("");
  const [price, setPrice] = useState<string>("");
  const [validity_days, setValidityDays] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  // Fetch channels from backend
  const fetchChannels = useCallback(async () => {
    setChannelsLoading(true);
    setChannelError(null);
    try {
      const list = await getAdminChannels();
      setChannels(list as unknown as Channel[]);
    } catch (e: any) {
      setChannelError(e?.message || "Failed to load channels");
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  // Fetch combos from backend
  const fetchCombos = useCallback(async () => {
    setCombosLoading(true);
    setCombosError(null);
    try {
      const serverCombos = await getAdminCombos();
      setCombos(serverCombos as Combo[]);
    } catch (e: any) {
      setCombosError(e?.message || "Failed to load combos");
      setCombos([]);
    } finally {
      setCombosLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChannels();
    fetchCombos();
  }, [fetchChannels, fetchCombos]);

  const resetForm = () => {
    setName("");
    setPrice("");
    setValidityDays("");
    setDescription("");
    setSelectedChannelIds([]);
    setFormError(null);
  };

  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const validate = (): boolean => {
    const errors: string[] = [];
    if (!name.trim()) errors.push("Name is required");
    const pr = Number(price);
    if (isNaN(pr)) errors.push("Price must be a number");
    if (!isNaN(pr) && pr < 0) errors.push("Price cannot be negative");
    const vd = Math.floor(Number(validity_days));
    if (!vd || isNaN(vd) || vd <= 0) errors.push("Validity days must be an integer > 0");
    if (selectedChannelIds.length === 0) errors.push("Select at least one channel");
    setFormError(errors[0] || null);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);

    try {
      const newCombo = await createAdminCombo({
        name: name.trim(),
        description: description.trim() || undefined,
        included_channels: selectedChannelIds,
        price: Number(price),
        validity_days: Math.floor(Number(validity_days)),
      });

      setCombos((prev) => [newCombo, ...prev]);
      handleClose();
    } catch (err: any) {
      setFormError(err?.message || "Failed to create combo");
    } finally {
      setIsLoading(false);
    }
  };

  const updateCombo = (comboId: string, updater: (c: Combo) => Combo) => {
    setCombos((prev) => prev.map((c) => (c._id === comboId ? updater({ ...c }) : c)));
  };

  const renderList = () => {
    if (combosLoading) return <LoadingIndicator text="Loading combos..." />;
    if (combosError) return <ErrorMessage message={combosError} />;
    if (!combos || combos.length === 0) return <EmptyState message="No combo plans yet. Create and manage combo plans here." />;

    return (
      <div className="space-y-4">
        {combos.map((combo) => (
          <ComboListItem key={combo._id} combo={combo} onUpdateCombo={updateCombo} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageTitle title="Combo Plans" subtitle="Manage bundled/combo plans." />
        <Button variant="primary" onClick={handleOpen}>+ Add Combo</Button>
      </div>

      {renderList()}

      <Modal isOpen={isModalOpen} onClose={handleClose} title="Add Combo Plan" size="3xl" modalClassName="bg-gradient-to-br from-dark-secondary via-dark-secondary to-dark-tertiary/20">
        <div className="mx-auto px-4 sm:px-6 md:p-6 w-full">
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 md:p-8 rounded-xl">
            {/* 1. Combo Information */}
            <div className="space-y-6 pb-4 border-b border-dark-tertiary/30">
              <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
                <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-golden-accent font-bold text-sm">1</span>
                </div>
                Combo Information
              </h2>
              <p className="text-text-secondary text-sm sm:ml-11">Basic combo details and description</p>
              <div className="sm:ml-11 space-y-6">
                <Input label="Combo Name" id="combo_name" value={name} onChange={(e) => setName(e.target.value)} required disabled={isLoading} maxLength={100} />
                <TextArea label="Description (Optional)" id="combo_description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} disabled={isLoading} maxLength={500} placeholder="Describe what this combo offers to users..." />
              </div>
            </div>

            {/* 2. Pricing */}
            <div className="space-y-6 mt-8 pb-4 border-b border-dark-tertiary/30">
              <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
                <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-golden-accent font-bold text-sm">2</span>
                </div>
                Pricing Configuration
              </h2>
              <p className="text-text-secondary text-sm sm:ml-11">Set the price for this combo plan</p>
              <div className="sm:ml-11">
                <div className="max-w-md">
                  <Input label="Price" id="combo_price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} required min={0} disabled={isLoading} placeholder="e.g., 1000.00" />
                  <div className="mt-2 text-xs text-text-secondary bg-dark-tertiary/30 p-2 sm:p-3 rounded-lg flex items-center gap-2">
                    <span>This is the price customers will pay for this combo</span>
                  </div>
                </div>
                {/* Price Preview */}
                {price && !isNaN(Number(price)) && (
                  <div className="mt-6 p-4 bg-dark-tertiary/20 rounded-lg border border-golden-accent/20">
                    <h3 className="text-sm font-semibold text-text-primary mb-2">Price Preview:</h3>
                    <span className="text-2xl font-bold text-golden-accent">₹{Number(price).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 3. Validity */}
            <div className="space-y-6 mt-8 pb-4 border-b border-dark-tertiary/30">
              <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
                <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-golden-accent font-bold text-sm">3</span>
                </div>
                Combo Duration
              </h2>
              <p className="text-text-secondary text-sm sm:ml-11">Set how long this combo remains active</p>
              <div className="sm:ml-11 max-w-md">
                <Input label="Validity (Days)" id="combo_validity_days" type="number" value={validity_days} onChange={(e) => setValidityDays(e.target.value)} required min={1} step={1} disabled={isLoading} placeholder="e.g., 30" />
                <div className="mt-2 text-xs text-text-secondary bg-dark-tertiary/30 p-2 sm:p-3 rounded-lg flex items-center gap-2">
                  <span>Number of days the combo will be active after purchase</span>
                </div>
              </div>
            </div>

            {/* 4. Channels */}
            <div className="space-y-6 mt-8">
              <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
                <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-golden-accent font-bold text-sm">4</span>
                </div>
                Channels Included
              </h2>
              <p className="text-text-secondary text-sm sm:ml-11">Select the channels included in this combo</p>
              <div className="sm:ml-11">
                <MultiSelect
                  label="Channels"
                  options={channels.map((c) => ({ value: c._id, label: c.name }))}
                  selectedValues={selectedChannelIds}
                  onChange={setSelectedChannelIds}
                  isLoading={channelsLoading}
                  placeholder={channelsLoading ? "Loading channels..." : "Select channels"}
                  error={channelError || undefined}
                />
              </div>
            </div>

            {/* Form Error */}
            {formError && (
              <div className="mt-8">
                <ErrorMessage message={formError} className="mb-0" title="Submission Error" />
              </div>
            )}

            {/* Submit/Cancel Buttons */}
            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={isLoading}>Cancel</Button>
              <Button type="submit" variant="primary" isLoading={isLoading} disabled={isLoading}>Create Combo</Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default AdminComboPlansPage;
